import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MdNoteAlt,
  MdLink,
  MdEmail,
  MdAdd,
  MdPushPin,
  MdOutlinePushPin,
  MdDelete,
  MdEdit,
  MdClose,
  MdContentCopy,
  MdCheck,
  MdSearch,
  MdNotes,
  MdOpenInFull,
} from "react-icons/md";
import { format } from "date-fns";
import { formatDistanceToNow } from "date-fns";
import api from "../services/api";
import toast from "react-hot-toast";
import "./Notepad.css";

const TYPE_META = {
  note:  { label: "Note",  Icon: MdNotes,  color: "#f59e0b", bg: "rgba(245,158,11,0.10)"  },
  link:  { label: "Link",  Icon: MdLink,   color: "#6366f1", bg: "rgba(99,102,241,0.10)"  },
  email: { label: "Email", Icon: MdEmail,  color: "#10b981", bg: "rgba(16,185,129,0.10)"  },
};

// Color keys stored in DB; CSS classes handle light/dark rendering
const COLORS = [
  { value: "",         label: "Default", preview: "var(--surface2)"  },
  { value: "yellow",   label: "Yellow",  preview: "#fef9c3"          },
  { value: "blue",     label: "Blue",    preview: "#dbeafe"          },
  { value: "green",    label: "Green",   preview: "#dcfce7"          },
  { value: "pink",     label: "Pink",    preview: "#fce7f3"          },
  { value: "purple",   label: "Purple",  preview: "#ede9fe"          },
];

// --- New Note Modal ---
const NoteModal = ({ onSave, onClose }) => {
  const [form, setForm] = useState({ title: "", content: "", type: "note", color: "" });
  const contentRef = useRef();

  useEffect(() => { contentRef.current?.focus(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.content.trim()) { toast.error("Content is required"); return; }
    onSave(form);
  };

  return (
    <div className="np-modal-overlay" onClick={onClose}>
      <div className="np-modal" onClick={(e) => e.stopPropagation()}>
        <div className="np-modal-header">
          <span className="np-modal-title">New Note</span>
          <button className="np-icon-btn" onClick={onClose}><MdClose size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="np-modal-body">
          {/* Type selector */}
          <div className="np-type-tabs">
            {Object.entries(TYPE_META).map(([key, { label, Icon, color }]) => (
              <button
                key={key}
                type="button"
                className={`np-type-tab ${form.type === key ? "active" : ""}`}
                style={form.type === key ? { borderColor: color, color } : {}}
                onClick={() => setForm((f) => ({ ...f, type: key }))}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
          </div>

          <input
            className="np-input"
            placeholder="Title (optional)"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <textarea
            ref={contentRef}
            className="np-textarea"
            placeholder={
              form.type === "link"  ? "Paste your URL here…"   :
              form.type === "email" ? "Paste the email address…" :
                                     "Write your note…"
            }
            rows={5}
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            required
          />

          {/* Color picker */}
          <div className="np-color-row">
            <span className="np-color-label">Card color:</span>
            {COLORS.map(({ value, label, preview }) => (
              <button
                key={label}
                type="button"
                title={label}
                className={`np-color-swatch ${form.color === value ? "selected" : ""}`}
                style={{ background: preview, border: "2px solid var(--border)" }}
                onClick={() => setForm((f) => ({ ...f, color: value }))}
              />
            ))}
          </div>

          <div className="np-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Note</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Edit inline modal ---
const EditModal = ({ note, onSave, onClose }) => {
  const [form, setForm] = useState({
    title: note.title,
    content: note.content,
    type: note.type,
    color: note.color || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.content.trim()) { toast.error("Content is required"); return; }
    onSave(form);
  };

  return (
    <div className="np-modal-overlay" onClick={onClose}>
      <div className="np-modal" onClick={(e) => e.stopPropagation()}>
        <div className="np-modal-header">
          <span className="np-modal-title">Edit Note</span>
          <button className="np-icon-btn" onClick={onClose}><MdClose size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="np-modal-body">
          <div className="np-type-tabs">
            {Object.entries(TYPE_META).map(([key, { label, Icon, color }]) => (
              <button
                key={key}
                type="button"
                className={`np-type-tab ${form.type === key ? "active" : ""}`}
                style={form.type === key ? { borderColor: color, color } : {}}
                onClick={() => setForm((f) => ({ ...f, type: key }))}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
          </div>
          <input
            className="np-input"
            placeholder="Title (optional)"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <textarea
            className="np-textarea"
            rows={5}
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            required
          />
          <div className="np-color-row">
            <span className="np-color-label">Card color:</span>
            {COLORS.map(({ value, label, preview }) => (
              <button
                key={label}
                type="button"
                title={label}
                className={`np-color-swatch ${form.color === value ? "selected" : ""}`}
                style={{ background: preview, border: "2px solid var(--border)" }}
                onClick={() => setForm((f) => ({ ...f, color: value }))}
              />
            ))}
          </div>
          <div className="np-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Full-content view modal ---
const ViewModal = ({ note, onEdit, onClose }) => {
  const [copied, setCopied] = useState(false);
  const { Icon, color, bg } = TYPE_META[note.type] || TYPE_META.note;
  const canCopy = note.type === "link" || note.type === "email";

  const handleCopy = () => {
    navigator.clipboard.writeText(note.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="np-modal-overlay" onClick={onClose}>
      <div
        className={`np-modal np-view-modal ${note.color ? `np-card--bg-${note.color}` : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="np-modal-header">
          <span className="np-card-type-badge" style={{ background: bg, color }}>
            <Icon size={13} />
            {TYPE_META[note.type]?.label}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {canCopy && (
              <button className="np-icon-btn" title="Copy content" onClick={handleCopy}>
                {copied ? <MdCheck size={18} color="#10b981" /> : <MdContentCopy size={18} />}
              </button>
            )}
            <button
              className="np-icon-btn"
              title="Edit this note"
              onClick={() => { onClose(); onEdit(note); }}
            >
              <MdEdit size={18} />
            </button>
            <button className="np-icon-btn" title="Close" onClick={onClose}>
              <MdClose size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="np-view-body">
          {note.title && <h2 className="np-view-title">{note.title}</h2>}
          <div className={`np-view-content ${note.type === "link" ? "np-card-content--link" : ""}`}>
            {note.type === "link" ? (
              <a href={note.content} target="_blank" rel="noopener noreferrer">{note.content}</a>
            ) : (
              note.content
            )}
          </div>
          <div className="np-view-meta">
            {note.isPinned && (
              <span className="np-pinned-label"><MdPushPin size={11} /> Pinned</span>
            )}
            <span className="np-card-time">
              Last updated {format(new Date(note.updatedAt), "dd MMM yyyy, hh:mm a")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Single note card ---
const NoteCard = ({ note, onPin, onEdit, onDelete, onView }) => {
  const [copied, setCopied] = useState(false);
  const { Icon, color, bg } = TYPE_META[note.type] || TYPE_META.note;
  const canCopy = note.type === "link" || note.type === "email";

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(note.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={`np-card ${note.isPinned ? "np-card--pinned" : ""} ${note.color ? `np-card--bg-${note.color}` : ""}`}
      onClick={() => onView(note)}
      style={{ cursor: "pointer" }}
    >
      {/* Card header */}
      <div className="np-card-header">
        <span className="np-card-type-badge" style={{ background: bg, color }}>
          <Icon size={13} />
          {TYPE_META[note.type]?.label}
        </span>
        <div className="np-card-actions" onClick={(e) => e.stopPropagation()}>
          {canCopy && (
            <button className="np-icon-btn" title="Copy to clipboard" onClick={handleCopy}>
              {copied ? <MdCheck size={16} color="#10b981" /> : <MdContentCopy size={16} />}
            </button>
          )}
          <button
            className="np-icon-btn"
            title="View full note"
            onClick={() => onView(note)}
          >
            <MdOpenInFull size={15} />
          </button>
          <button
            className="np-icon-btn"
            title={note.isPinned ? "Unpin" : "Pin to top"}
            onClick={() => onPin(note)}
          >
            {note.isPinned
              ? <MdPushPin size={16} color="#6366f1" />
              : <MdOutlinePushPin size={16} />}
          </button>
          <button className="np-icon-btn" title="Edit" onClick={() => onEdit(note)}>
            <MdEdit size={16} />
          </button>
          <button className="np-icon-btn np-icon-btn--danger" title="Delete" onClick={() => onDelete(note._id)}>
            <MdDelete size={16} />
          </button>
        </div>
      </div>

      {/* Title */}
      {note.title && <div className="np-card-title">{note.title}</div>}

      {/* Content preview (truncated) */}
      <div className={`np-card-content ${note.type === "link" ? "np-card-content--link" : ""}`}>
        {note.type === "link" ? (
          <a
            href={note.content}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {note.content}
          </a>
        ) : (
          note.content
        )}
      </div>

      {/* Footer */}
      <div className="np-card-footer">
        {note.isPinned && <span className="np-pinned-label"><MdPushPin size={11} /> Pinned</span>}
        <span className="np-card-time">
          {formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })}
        </span>
      </div>
    </div>
  );
};

// --- Main Notepad page ---
const Notepad = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchNotes = useCallback(async () => {
    try {
      const { data } = await api.get("/notepad");
      setNotes(data.data);
    } catch {
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleSave = async (form) => {
    try {
      const { data } = await api.post("/notepad", form);
      setNotes((prev) => [data.data, ...prev]);
      setShowModal(false);
      toast.success("Note saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save note");
    }
  };

  const handleUpdate = async (form) => {
    try {
      const { data } = await api.put(`/notepad/${editNote._id}`, form);
      setNotes((prev) => prev.map((n) => n._id === editNote._id ? data.data : n));
      setEditNote(null);
      toast.success("Note updated");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update note");
    }
  };

  const handlePin = async (note) => {
    try {
      const { data } = await api.put(`/notepad/${note._id}`, { isPinned: !note.isPinned });
      setNotes((prev) => {
        const updated = prev.map((n) => n._id === note._id ? data.data : n);
        return [...updated].sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0));
      });
    } catch {
      toast.error("Failed to update pin");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/notepad/${id}`);
      setNotes((prev) => prev.filter((n) => n._id !== id));
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  const filtered = notes.filter((n) => {
    if (typeFilter !== "all" && n.type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!n.title?.toLowerCase().includes(q) && !n.content.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const counts = { all: notes.length, note: 0, link: 0, email: 0 };
  notes.forEach((n) => { if (counts[n.type] !== undefined) counts[n.type]++; });

  return (
    <main className="page np-page">
      {/* Header */}
      <div className="np-header">
        <div className="np-header-left">
          <MdNoteAlt size={28} className="np-header-icon" />
          <div>
            <h1 className="np-header-title">My Notepad</h1>
            <p className="np-header-sub">Your private space for notes, links, and emails</p>
          </div>
        </div>
        <button className="btn btn-primary np-add-btn" onClick={() => setShowModal(true)}>
          <MdAdd size={20} />
          New Note
        </button>
      </div>

      {/* Filters */}
      <div className="np-toolbar">
        <div className="np-type-filter">
          {["all", "note", "link", "email"].map((t) => (
            <button
              key={t}
              className={`np-filter-tab ${typeFilter === t ? "active" : ""}`}
              onClick={() => setTypeFilter(t)}
            >
              {t === "all" ? "All" : TYPE_META[t].label}
              <span className="np-filter-count">{counts[t]}</span>
            </button>
          ))}
        </div>
        <div className="np-search-wrap">
          <MdSearch size={18} className="np-search-icon" />
          <input
            className="np-search"
            placeholder="Search notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="np-search-clear" onClick={() => setSearch("")}>
              <MdClose size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading"><div className="spinner" /><span className="loading-text">Loading…</span></div>
      ) : filtered.length === 0 ? (
        <div className="np-empty">
          <MdNoteAlt size={56} className="np-empty-icon" />
          <h3>{notes.length === 0 ? "Your notepad is empty" : "No notes match your search"}</h3>
          <p>{notes.length === 0 ? "Click \"New Note\" to save your first note, link, or email." : "Try a different search term or filter."}</p>
          {notes.length === 0 && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <MdAdd size={18} /> Create First Note
            </button>
          )}
        </div>
      ) : (
        <div className="np-grid">
          {filtered.map((note) => (
            <NoteCard
              key={note._id}
              note={note}
              onPin={handlePin}
              onEdit={setEditNote}
              onDelete={handleDelete}
              onView={setViewNote}
            />
          ))}
        </div>
      )}

      {showModal && <NoteModal onSave={handleSave} onClose={() => setShowModal(false)} />}
      {editNote && <EditModal note={editNote} onSave={handleUpdate} onClose={() => setEditNote(null)} />}
      {viewNote && (
        <ViewModal
          note={viewNote}
          onEdit={(n) => { setViewNote(null); setEditNote(n); }}
          onClose={() => setViewNote(null)}
        />
      )}
    </main>
  );
};

export default Notepad;
